<template>
  <div class="form-page-container">
    <PremiumPage
      title="Edit Expense"
      subtitle="Update existing transaction details"
      showBack
      show-footer-buttons
      submit-label="Submit"
      :loading="loading"
      @cancel="$router.go(-1)"
      @submit="saveExpenseDetail"
    >
      <div class="pa-4">
        <v-form>
          <div class="half-width-wrap">
            <v-row>
              <v-col cols="12">
                <PremiumInput
                  v-model="expense.title"
                  label="Expense Title *"
                  placeholder="e.g. Monthly Electricity Bill"
                  icon="mdi-format-title"
                  v-validate="'required'"
                  name="title"
                  :error="errors.first('title') || serverSideErrors.title"
                />
              </v-col>
              <v-col cols="12">
                <PremiumInput
                  v-model="expense.note"
                  label="Note / Description *"
                  placeholder="Details of the expense..."
                  multiline
                  rows="3"
                  resizable
                  v-validate="'required'"
                  name="note"
                  :error="errors.first('note') || serverSideErrors.note"
                />
              </v-col>
            </v-row>

            <v-row class="mt-4">
              <v-col cols="12">
                <PremiumInput
                  v-model="expense.amount"
                  label="Amount (₹) *"
                  placeholder="0.00"
                  icon="mdi-currency-inr"
                  v-validate="'required|decimal|max_value:9999999'"
                  name="amount"
                  type="number"
                  :error="errors.first('amount') || serverSideErrors.amount"
                />
              </v-col>

              <v-col cols="12">
                <div class="premium-field-label mb-2">Category *</div>
                <div class="d-flex align-center gap-2">
                  <v-select
                    v-model="expense.category_id"
                    :items="categories"
                    item-text="name"
                    item-value="id"
                    placeholder="Select Category"
                    outlined
                    dense
                    hide-details="auto"
                    v-validate="'required'"
                    name="category"
                    class="premium-select flex-grow-1"
                    :error="
                      errors.first('category') != null ||
                      serverSideErrors.category_id != null
                    "
                  />
                  <v-btn
                    depressed
                    color="var(--surface2)"
                    class="add-cat-btn"
                    @click="addCategoryDialog = true"
                  >
                    <v-icon color="var(--brand)">mdi-plus</v-icon>
                  </v-btn>
                </div>
                <div
                  class="error-text"
                  v-if="
                    errors.first('category') || serverSideErrors.category_id
                  "
                >
                  {{ errors.first("category") || serverSideErrors.category_id }}
                </div>
              </v-col>
            </v-row>

            <v-row class="mt-4">
              <v-col cols="12">
                <PremiumDateTimePicker
                  v-model="expense.date"
                  label="Expense Date"
                  placeholder="Select date & time"
                  v-validate="'required'"
                  name="date"
                  :error="errors.first('date') || serverSideErrors.date"
                />
              </v-col>
            </v-row>
          </div>

          <!-- ── Attachments ── -->
          <v-row class="mt-8">
            <v-col cols="6">
              <div class="premium-field-label mb-4">Current Attachments</div>
              <div class="doc-grid mb-6" v-if="added_documents.length">
                <div
                  v-for="(item, idx) in added_documents"
                  :key="item.document_url"
                  class="doc-card"
                  style="width: 200px !important; height: 200px !important"
                >
                  <v-img
                    v-if="item.file_type == 'image'"
                    :src="base_url + '/uploads/' + item.document_url"
                    class="doc-card-img"
                    contain
                  />
                  <div v-else class="doc-card-placeholder">
                    <v-icon color="#EF4444" size="52">mdi-file-pdf-box</v-icon>
                    <span class="doc-card-placeholder-label">PDF</span>
                  </div>
                  <div class="doc-card-overlay">
                    <button type="button" class="doc-overlay-btn" @click.stop="imgClick(idx)">
                      <v-icon size="16" color="white">mdi-eye-outline</v-icon>
                    </button>
                    <button type="button" class="doc-overlay-btn danger" @click.stop="confirmDelete(item.id)">
                      <v-icon size="16" color="white">mdi-delete-outline</v-icon>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Upload New Slot -->
              <div class="premium-field-label mb-2">Attachments</div>
              <input
                type="file"
                ref="attachInput"
                multiple
                accept="image/*,.pdf"
                hidden
                @change="onAttachChange"
              />
              <!-- Drop Zone -->
              <div
                class="attach-dropzone"
                :class="{ 'attach-dropzone-active': dragging }"
                @click="$refs.attachInput.click()"
                @dragover.prevent="dragging = true"
                @dragleave="dragging = false"
                @drop.prevent="onDrop"
              >
                <v-icon size="28" color="var(--tx3)">mdi-paperclip</v-icon>
                <div class="dropzone-label">Click or drag files here</div>
                <div class="dropzone-hint">JPG, PNG, PDF allowed</div>
              </div>

              <!-- Preview Grid -->
              <div v-if="attachPreviews.length" class="attach-preview-grid mt-3">
                <div
                  v-for="(p, i) in attachPreviews"
                  :key="i"
                  class="attach-preview-item"
                >
                  <img v-if="p.isImage" :src="p.url" class="attach-preview-img" />
                  <div v-else class="attach-preview-pdf">
                    <v-icon size="32" color="#EF4444">mdi-file-pdf-box</v-icon>
                    <span class="attach-preview-name">{{ p.name }}</span>
                  </div>
                  <button type="button" class="attach-remove-btn" @click.stop="removeAttach(i)">
                    <v-icon size="14" color="white">mdi-close</v-icon>
                  </button>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-form>
      </div>

      <template #footer>
        <v-btn text class="btn-premium-cancel" @click="$router.go(-1)"
          >Cancel</v-btn
        >
        <v-btn
          depressed
          :loading="loading"
          class="btn-premium-primary ml-3"
          @click="saveExpenseDetail"
        >
          Submit
        </v-btn>
      </template>
    </PremiumPage>

    <!-- ── Confirm Modal ── -->
    <ConfirmModal
      v-model="deleteDialog"
      type="danger"
      title="Delete Document"
      message="Are you sure you want to delete this attachment?"
      @confirm="deleteDocument(delete_id)"
    />

    <AddCategoryDialog
      :dialog.sync="addCategoryDialog"
      :categories="categories"
      @category-added="handleCategoryAdded"
      @alert="handleAlert"
    />

    <!-- ── Media Viewer ── -->
    <MediaViewer
      v-model="imageDialog"
      :items="mediaItems"
      :index="mediaIndex"
    />
  </div>
</template>

<script>
import { mapMutations } from "vuex";
import moment from "moment";
import PremiumPage from "../../common/PremiumPage.vue";
import PremiumInput from "../../common/PremiumInput.vue";
import PremiumDateTimePicker from "../../common/PremiumDateTimePicker.vue";
import ConfirmModal from "../../common/ConfirmModal.vue";
import AddCategoryDialog from "@/components/societyadmin/expense/AddCategory.vue";
import MediaViewer from "../../common/MediaViewer.vue";

export default {
  name: "EditExpense",
  components: {
    PremiumPage,
    PremiumInput,
    PremiumDateTimePicker,
    ConfirmModal,
    AddCategoryDialog,
    MediaViewer,
  },
  data() {
    return {
      user: {},
      loading: false,
      serverSideErrors: {},
      expense: {
        title: "",
        note: "",
        date: null,
        amount: "",
        category_id: null,
        documents: [],
      },
      added_documents: [],
      delete_id: "",
      deleteDialog: false,
      base_url: process.env.VUE_APP_API_URL,
      categories: [],
      addCategoryDialog: false,
      attachPreviews: [],
      dragging: false,
      imageDialog: false,
      mediaIndex: 0,
    };
  },
  computed: {
    mediaItems() {
      return this.added_documents.map((doc) => ({
        url: this.base_url + "/uploads/" + doc.document_url,
        type: doc.file_type === "image" ? "image" : "document",
        name: doc.document_url ? doc.document_url.split("/").pop() : "file",
      }));
    },
  },
  methods: {
    ...mapMutations("alert", ["setAlert"]),
    handleAlert(alertData) {
      this.setAlert(alertData);
    },
    handleCategoryAdded() {
      this.getCategories();
    },
    onAttachChange(e) {
      this.addFiles(Array.from(e.target.files));
      e.target.value = "";
    },
    onDrop(e) {
      this.dragging = false;
      this.addFiles(Array.from(e.dataTransfer.files));
    },
    addFiles(files) {
      files.forEach((file) => {
        this.expense.documents.push(file);
        const isImage = file.type.startsWith("image/");
        if (isImage) {
          const url = URL.createObjectURL(file);
          this.attachPreviews.push({ url, name: file.name, isImage: true });
        } else {
          this.attachPreviews.push({
            url: null,
            name: file.name,
            isImage: false,
          });
        }
      });
    },
    removeAttach(i) {
      this.expense.documents.splice(i, 1);
      this.attachPreviews.splice(i, 1);
    },
    saveExpenseDetail() {
      let _self = this;
      this.$validator.validateAll().then((result) => {
        if (result) {
          _self.loading = true;
          let formData = new FormData();

          formData.append("title", this.expense.title);
          formData.append("note", this.expense.note);
          formData.append("date", moment.utc(this.expense.date).valueOf());
          formData.append("amount", this.expense.amount);
          formData.append("category_id", this.expense.category_id || "");

          if (this.expense.documents) {
            for (let i = 0; i < this.expense.documents.length; i++) {
              formData.append("documents[]", this.expense.documents[i]);
            }
          }

          _self.$axios
            .put("/societyadmin/expenses/" + _self.$route.params.id, formData, {
              headers: { "Content-Type": "multipart/form-data" },
            })
            .then((response) => {
              let data = response.data;
              _self.loading = false;
              _self.serverSideErrors = {};

              if (data.status) {
                this.setAlert({
                  showAlert: true,
                  message: data.message,
                  variant: "green accent-4",
                  timeout: 2000,
                });
                _self.$router.push({ path: "/societyadmin/expense" });
              } else {
                if (!data.errors) {
                  this.setAlert({
                    showAlert: true,
                    message: data.message,
                    variant: "red accent-3",
                    timeout: 2000,
                  });
                } else {
                  for (let i = 0; i < data.errors.length; i++) {
                    this.serverSideErrors[data.errors[i].field] =
                      data.errors[i].message;
                  }
                }
              }
            });
        }
      });
    },
    getExpenseDetail() {
      let _self = this;
      _self.$axios
        .get("societyadmin/expenses/" + _self.$route.params.id)
        .then((response) => {
          let data = response.data;
          if (data.status) {
            _self.expense = data.expense;
            _self.expense.date = new Date(data.expense.date);
            _self.added_documents = _self.filterActiveDocuments(data.expense.documents || []);
            // Reset documents to upload
            _self.expense.documents = [];
          }
        });
    },
    confirmDelete(id) {
      this.delete_id = id;
      this.deleteDialog = true;
    },
    filterActiveDocuments(docs) {
      return (docs || []).filter((doc) => !doc.deleted);
    },
    deleteDocument(Id) {
      let _self = this;
      _self.$axios
        .delete("societyadmin/expenses/documents/" + Id)
        .then((response) => {
          let data = response.data;
          if (data.status) {
            _self.added_documents = (_self.added_documents || []).filter(
              (doc) => doc.id !== Id,
            );
            if (_self.mediaIndex >= _self.added_documents.length) {
              _self.mediaIndex = Math.max(0, _self.added_documents.length - 1);
            }
            _self.deleteDialog = false;
            _self.setAlert({
              showAlert: true,
              variant: "green accent-4",
              timeout: "2000",
              message: data.message,
            });
          }
        });
    },
    imgClick(idx) {
      this.mediaIndex = idx;
      this.imageDialog = true;
    },
    getCategories() {
      let _self = this;
      _self.$axios
        .get("societyadmin/categories?recordsPerPage=999999")
        .then((response) => {
          let data = response.data;
          if (data.status) {
            _self.categories = data.categories;
          }
        });
    },
  },
  mounted() {
    this.user = JSON.parse(localStorage.getItem("user"));
    this.getExpenseDetail();
    this.getCategories();
  },
};
</script>

<style scoped>
.form-page-container {
  padding: 24px;
}

/* ── 50% width wrapper ── */
.half-width-wrap {
  max-width: 50%;
}
@media (max-width: 960px) {
  .half-width-wrap {
    max-width: 100%;
  }
}

.premium-field-label {
  font-size: 12px;
  font-weight: 700;
  color: var(--tx3, #64748b);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* ── Custom Textarea ── */
::v-deep .premium-textarea .v-input__slot {
  border-radius: 12px !important;
  background: var(--surface2, #f8fafc) !important;
  border: 1px solid var(--border, #e2e8f0) !important;
  box-shadow: none !important;
}
::v-deep .premium-textarea fieldset {
  border: none;
}

/* ── Select ── */
::v-deep .premium-select .v-input__slot {
  border-radius: 12px !important;
  background: var(--surface2, #f8fafc) !important;
  border: 1px solid var(--border, #e2e8f0) !important;
}
::v-deep .premium-select fieldset {
  border: none;
}
.add-cat-btn {
  height: 40px !important;
  min-width: 40px !important;
  width: 40px !important;
  border-radius: 8px !important;
  border: 1px solid var(--border) !important;
  margin-left: 8px;
}

/* ── DateTime Picker ── */
::v-deep .premium-datetime-picker .v-input__slot {
  border-radius: 12px !important;
  background: var(--surface2, #f8fafc) !important;
  border: 1px solid var(--border, #e2e8f0) !important;
  min-height: 48px !important;
  padding: 0 12px !important;
}
::v-deep .premium-datetime-picker input {
  font-size: 14px !important;
  font-weight: 600 !important;
}

/* ── Attachment Uploader ── */
.attach-dropzone {
  border: 2px dashed var(--border, #e2e8f0);
  border-radius: 14px;
  background: var(--surface2, #f8fafc);
  padding: 24px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
}
.attach-dropzone:hover {
  border-color: var(--brand);
  background: rgba(13, 153, 114, 0.04);
}
.attach-dropzone-active {
  border-color: var(--brand) !important;
  background: rgba(13, 153, 114, 0.06) !important;
}
.dropzone-label {
  font-size: 13px;
  font-weight: 700;
  color: var(--tx2);
  margin-top: 4px;
}
.dropzone-hint {
  font-size: 11.5px;
  color: var(--tx3);
  font-weight: 500;
}

.attach-preview-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.attach-preview-item {
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid var(--border);
  flex-shrink: 0;
  background: var(--surface2);
}
.attach-preview-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.attach-preview-pdf {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 4px;
}
.attach-preview-name {
  font-size: 9px;
  color: var(--tx3);
  text-align: center;
  word-break: break-all;
  line-height: 1.2;
}
.attach-remove-btn {
  position: absolute;
  top: 3px;
  right: 3px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s;
}
.attach-remove-btn:hover {
  background: #ef4444;
}

/* ── Attachments Grid ── */
.attachment-card-premium {
  width: 200px;
  height: 200px;
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  aspect-ratio: 1;
  background: var(--surface2);
  border: 1px solid var(--border);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}
.attachment-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.attachment-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fef2f2;
}
.attachment-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;
}
.attachment-card-premium:hover .attachment-overlay {
  opacity: 1;
}

.error-text {
  font-size: 12px;
  color: #ef4444;
  margin-top: 4px;
  font-weight: 500;
}

.cancel-btn {
  color: var(--tx2);
  font-weight: 600;
  border-radius: 10px;
  text-transform: none;
}
.save-btn {
  border-radius: 8px !important;
  font-size: 14px;
  font-weight: 700;
  padding: 0 32px !important;
  height: 44px !important;
  text-transform: none !important;
}
</style>
